import { format, formatDistanceToNow } from "date-fns";

/**
 * Format a date to a human-readable string
 * @param date The date to format
 * @param formatStr The format string (default: 'MMM d, yyyy')
 * @returns Formatted date string
 */
export const formatDate = (
  date: Date | string | number,
  formatStr = "MMM d, yyyy",
): string => {
  try {
    const dateObj =
      typeof date === "string" || typeof date === "number"
        ? new Date(date)
        : date;
    return format(dateObj, formatStr);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid date";
  }
};

/**
 * Format a datetime to a human-readable string
 * @param date The date to format
 * @param formatStr The format string (default: 'MMM d, yyyy h:mm a')
 * @returns Formatted datetime string
 */
export function formatDateTime(
  date: Date | string | number,
  formatStr = "MMM d, yyyy h:mm a",
): string {
  try {
    if (!date) return "";

    const dateObj =
      typeof date === "string" || typeof date === "number"
        ? new Date(date)
        : date;

    // For simple ISO string format without custom format string
    if (arguments.length === 1 && typeof date === "string") {
      return dateObj.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    return format(dateObj, formatStr);
  } catch (error) {
    console.error("Error formatting datetime:", error);
    return "Invalid date";
  }
}

/**
 * Format a date relative to the current time
 * @param date The date to format
 * @returns Relative time string (e.g., "2 days ago")
 */
export const formatRelativeTime = (date: Date | string | number): string => {
  try {
    const dateObj =
      typeof date === "string" || typeof date === "number"
        ? new Date(date)
        : date;
    return formatDistanceToNow(dateObj, { addSuffix: true });
  } catch (error) {
    console.error("Error formatting relative time:", error);
    return "Invalid date";
  }
};

/**
 * Format a number as currency
 * @param value The number to format
 * @param currency The currency code (default: 'USD')
 * @param locale The locale (default: 'en-US')
 * @returns Formatted currency string
 */
export const formatCurrency = (
  value: number,
  currency = "USD",
  locale = "en-US",
): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(value);
  } catch (error) {
    console.error("Error formatting currency:", error);
    return `${value}`;
  }
};

/**
 * Formats a number with specified decimal places
 * @param value - The number to format
 * @param decimals - Number of decimal places
 * @returns Formatted number as string
 */
export const formatNumber = (value: number, decimals = 0): string => {
  if (value === undefined || value === null || isNaN(value)) return "0";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format a number as a percentage
 * @param value The number to format (e.g., 0.75 for 75%)
 * @param decimals The number of decimal places (default: 2)
 * @param locale The locale (default: 'en-US')
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number,
  decimals = 2,
  locale = "en-US",
): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: "percent",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  } catch (error) {
    console.error("Error formatting percentage:", error);
    return `${(value * 100).toFixed(decimals)}%`;
  }
};

/**
 * Truncate a string to a specified length
 * @param str The string to truncate
 * @param length The maximum length (default: 50)
 * @param suffix The suffix to add if truncated (default: '...')
 * @returns Truncated string
 */
export const truncateString = (
  str: string,
  length = 50,
  suffix = "...",
): string => {
  if (!str) return "";
  if (str.length <= length) return str;
  return str.substring(0, length - suffix.length) + suffix;
};

/**
 * Convert camelCase to Title Case
 * @param str The camelCase string
 * @returns Title Case string
 */
export const camelToTitleCase = (str: string): string => {
  if (!str) return "";
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase());
};

/**
 * Format file size to human-readable string
 * @param bytes File size in bytes
 * @param decimals Number of decimal places (default: 2)
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

/**
 * Formats a duration in milliseconds to a readable format
 * @param ms - Duration in milliseconds
 * @returns Formatted duration string
 */
export const formatDuration = (ms: number): string => {
  if (!ms) return "0s";

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};
